import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { GoogleOAuthProvider } from '@react-oauth/google';
import TermsModal from './components/TermsModal';
import { useTermsAcceptance } from './hooks/useTermsAcceptance';
//import theme from './theme';
import {theme} from './constants/theme';
import './i18n/config'; // Initialize i18n
import LanguageLoader from './components/LanguageLoader';
import Navbar from './components/Navbar';
import MemoriesNavbar from './components/MemoriesNavbar';
import MemoriesHostRoutes from './components/MemoriesHostRoutes';
import DeveloperHostRoutes from './components/DeveloperHostRoutes';
import CatalogEmbed from './pages/developer/CatalogEmbed';
import ScrollToTop from './components/ScrollToTop';
import WhatsNewBanner from './components/WhatsNewBanner';
import BrandedHomeGate from './components/BrandedHomeGate';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import MemoriesAuthBridge from './pages/MemoriesAuthBridge';
import MemoriesAuthLogout from './pages/MemoriesAuthLogout';
import UserProfile from './pages/UserProfile';
import Preferences from './pages/Preferences';
import CreateTrip from './pages/CreateTrip';
import Trips from './pages/Trips';
import TripDetails from './pages/TripDetails';
import PublicTripView from './pages/PublicTripView';
import TripRating from './pages/TripRating';
import EditTrip from './pages/EditTrip';
import EmailImports from './pages/EmailImports';
import MemoriesList from './pages/MemoriesList';
import MemoriesCreate from './pages/MemoriesCreate';
import MemoriesEvent from './pages/MemoriesEvent';
import MemoriesUpload from './pages/MemoriesUpload';
import MemoriesJoin from './pages/MemoriesJoin';
import MemoriesShare from './pages/MemoriesShare';
import { isMemoriesHost } from './utils/memoriesHost';
import { isDeveloperHost } from './utils/developerHost';
import { shouldHideAppNavbar } from './utils/appChrome';
import { shouldUsePartnerChrome } from './utils/partnerWorkspace';
import Support from './pages/Support';
import HowToGuide from './pages/HowToGuide';
import PrivateRoute from './components/PrivateRoute';
import MemoriesGuestOrUserRoute from './components/MemoriesGuestOrUserRoute';
import { AuthProvider } from './contexts/AuthContext';
import { ClientBrandingProvider } from './contexts/ClientBrandingContext';
import BrandThemeSync from './components/BrandThemeSync';
import AppPageFooter from './components/AppPageFooter';
import { GlobalWebSocketProvider } from './contexts/GlobalWebSocketContext';
import { UnreadMessagesProvider, useUnreadMessagesContext } from './contexts/UnreadMessagesContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { BypassProvider } from './contexts/BypassContext';
import { TokenManager } from './utils/tokenManager';
import GlobalNotificationSubscriber from './components/GlobalNotificationSubscriber';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import PaymentFailed from './pages/PaymentFailed';
import Pricing from './pages/Pricing';
import AIConciergeRedirect from './components/AIConciergeRedirect';
import Terms from './pages/Terms';
import PartnerTerms from './pages/PartnerTerms';
import DealsPlan from './pages/DealsPlan';
import AdvisorDirectory from './pages/AdvisorDirectory';
import PublicAdvisorProfile from './pages/PublicAdvisorProfile';
import DuffelOffer from './pages/DuffelOffer';
import FlightBookingConfirmation from './pages/FlightBookingConfirmation';
import FlightSearchResults from './pages/FlightSearchResults';
import FlightFareSelector from './pages/FlightFareSelector';
import PackageDetail from './pages/PackageDetail';
import MyBookings from './pages/MyBookings';
import MyPackageBookingDetail from './pages/MyPackageBookingDetail';
import MyInquiries from './pages/MyInquiries';
import BookingDetails from './pages/BookingDetails';
import Privacy from './pages/Privacy';
import AdminPage from './pages/AdminPage';
import AdminPartnerApplications from './pages/AdminPartnerApplications';
import AdminCampaigns from './pages/AdminCampaigns';
import AdminMarketingShows from './pages/AdminMarketingShows';
import AdminPrivateTravel from './pages/AdminPrivateTravel';
import AdminFeatures from './pages/AdminFeatures';
import PartnerPrivateReferrals from './pages/PartnerPrivateReferrals';
import PartnerLogin from './pages/PartnerLogin';
import PartnerPasswordSetup from './pages/PartnerPasswordSetup';
import PartnerDashboard from './pages/PartnerDashboard';
import PartnerProfile from './pages/PartnerProfile';
import PartnerTrips from './pages/PartnerTrips';
import PartnerTripDetails from './pages/PartnerTripDetails';
import PartnerPackages from './pages/PartnerPackages';
import PartnerDmcs from './pages/PartnerDmcs';
import PartnerPackageEditor from './pages/PartnerPackageEditor';
import PublicPackageDetails from './pages/PublicPackageDetails';
import PartnerCatalog from './pages/PartnerCatalog';
import PartnerInquiries from './pages/PartnerInquiries';
import PartnerRequests from './pages/PartnerRequests';
import PartnerBookings from './pages/PartnerBookings';
import PartnerTeam from './pages/PartnerTeam';
import PartnerInventory from './pages/PartnerInventory';
import PartnerAddOns from './pages/PartnerAddOns';
import PartnerAnalytics from './pages/PartnerAnalytics';
import PartnerClients from './pages/PartnerClients';
import PartnerClientDetail from './pages/PartnerClientDetail';
import PartnerIntakePublic from './pages/PartnerIntakePublic';
import PartnerBilling from './pages/PartnerBilling';
import TripPlanner from './pages/TripPlanner';
import GroupVacation from './pages/GroupVacation';
import Partner from './pages/Partner';
import PartnerMasterTrips from './pages/PartnerMasterTrips';
import WTMHub from './pages/WTMHub';
import EventMarketingPage from './pages/EventMarketingPage';
import PartnerMasterTripDetail from './pages/PartnerMasterTripDetail';
import Forum from './pages/Forum';
import ForumPost from './pages/ForumPost';
import ForumNewPost from './pages/ForumNewPost';
import TravelGuides from './pages/TravelGuides';
import TravelGuideDetail from './pages/TravelGuideDetail';
import Destinations from './pages/Destinations';
import DestinationDetail from './pages/DestinationDetail';
import EventGuides from './pages/EventGuides';
import EventGuideDetail from './pages/EventGuideDetail';
import PartnerSupportBanner from './components/PartnerSupportBanner';

const AppChrome = () => {
  const { pathname } = useLocation();
  if (isDeveloperHost()) return null;
  if (isMemoriesHost()) return <MemoriesNavbar />;
  const partnerSession = typeof window !== 'undefined' && localStorage.getItem('partnerToken');
  const hideWhatsNewBanner = shouldUsePartnerChrome(pathname, partnerSession);
  const onDeveloperPath = pathname === '/developer' || pathname.startsWith('/developer/');
  return (
    <>
      {hideWhatsNewBanner || onDeveloperPath ? null : <WhatsNewBanner />}
      {onDeveloperPath ? null : (shouldHideAppNavbar(pathname) ? null : <Navbar />)}
    </>
  );
};

// Component to handle navigation-based refresh of unread messages
const NavigationRefreshHandler = () => {
  const location = useLocation();
  const { refresh } = useUnreadMessagesContext();
  const previousPathnameRef = useRef(location.pathname);

  useEffect(() => {
    // Check if pathname has actually changed (not just search params or hash)
    if (previousPathnameRef.current !== location.pathname) {
      console.log('🧭 [NavigationRefresh] Page navigation detected:', {
        from: previousPathnameRef.current,
        to: location.pathname
      });

      // Small delay to allow components to mount/unmount during navigation
      const refreshTimeout = setTimeout(() => {
        console.log('🔄 [NavigationRefresh] Refreshing unread counts after navigation');
        refresh();
      }, 150);

      // Update the previous pathname
      previousPathnameRef.current = location.pathname;

      return () => clearTimeout(refreshTimeout);
    }
  }, [location.pathname, refresh]);

  return null;
};

// Global AI Results Notification Component
const GlobalAIResultsListener = () => {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'premium_ai_recommendations' && e.newValue) {
        try {
          const recommendations = JSON.parse(e.newValue);
          console.log('🎉 Global AI results received via storage event:', recommendations);
          
          // Show success snackbar
          enqueueSnackbar('🎉 Your AI trip recommendations are ready!', {
            variant: 'success',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'right' }
          });
        } catch (error) {
          console.error('Error parsing AI results from storage:', error);
        }
      }
    };

    const handleCustomAIResults = (e) => {
      try {
        const recommendations = e.detail;
        console.log('🎉 Global AI results received via custom event:', recommendations);
        
        // Show success snackbar
        enqueueSnackbar('🎉 Your AI trip recommendations are ready!', {
          variant: 'success',
          autoHideDuration: 5000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
      } catch (error) {
        console.error('Error parsing AI results from custom event:', error);
      }
    };

    // Listen for both storage events (cross-tab) and custom events (same-tab)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('ai-results-ready', handleCustomAIResults);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('ai-results-ready', handleCustomAIResults);
    };
  }, [enqueueSnackbar]);

  return null; // This component doesn't render anything
};

function App() {
  const { 
    showTermsModal, 
    handleAcceptTerms, 
    handleCloseTermsModal 
  } = useTermsAcceptance();

  useEffect(() => {
    // Setup automatic token expiry checking
    const cleanup = TokenManager.setupTokenExpiryCheck();
    
    // Cleanup on component unmount
    return cleanup;
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <HelmetProvider>
      <AuthProvider>
        <GlobalWebSocketProvider>
              <UnreadMessagesProvider>
                  <PaymentProvider>
                    <BypassProvider>
                      <ThemeProvider theme={theme}>
                <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                  <LanguageLoader />
                  <GlobalAIResultsListener />
                  <CssBaseline />
                  <PerformanceOptimizer />
                  <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
                    <ClientBrandingProvider>
                    <BrandThemeSync />
                    <ScrollToTop />
                    <NavigationRefreshHandler />
                    <GlobalNotificationSubscriber />
                    <AppChrome />
                    <PartnerSupportBanner />
                    {isDeveloperHost() ? (
                      <DeveloperHostRoutes />
                    ) : isMemoriesHost() ? (
                      <MemoriesHostRoutes />
                    ) : (
                    <>
                    <Routes>
                <Route path="/" element={<BrandedHomeGate />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/trip-planner" element={<TripPlanner />} />
                <Route path="/group-vacation" element={<GroupVacation />} />
                <Route path="/partner" element={<Partner />} />
                <Route path="/shows" element={<WTMHub />} />
                <Route path="/shows/:slug" element={<EventMarketingPage />} />
                <Route path="/wtm" element={<Navigate to="/shows" replace />} />
                <Route path="/wtm-dubai" element={<Navigate to="/shows" replace />} />
                <Route path="/wtm-london" element={<Navigate to="/shows" replace />} />
                <Route path="/partner-guide" element={<Navigate to="/partner" replace />} />
                <Route path="/forum" element={<Forum />} />
                <Route path="/forum/new" element={<PrivateRoute><ForumNewPost /></PrivateRoute>} />
                <Route path="/forum/:slug" element={<ForumPost />} />
                <Route path="/guides" element={<TravelGuides />} />
                <Route path="/guides/:slug" element={<TravelGuideDetail />} />
                <Route path="/destinations" element={<Destinations />} />
                <Route path="/destinations/:slug" element={<DestinationDetail />} />
                <Route path="/event-guides" element={<EventGuides />} />
                <Route path="/event-guides/:slug" element={<EventGuideDetail />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/auth/memories-bridge" element={<MemoriesAuthBridge />} />
                <Route path="/auth/memories-logout" element={<MemoriesAuthLogout />} />
                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <UserProfile />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/preferences"
                  element={
                    <PrivateRoute>
                      <Preferences />
                    </PrivateRoute>
                  }
                />
                <Route path="/trips/create" element={<PrivateRoute><CreateTrip /></PrivateRoute>} />
                <Route path="/trips/create/:date" element={<PrivateRoute><CreateTrip /></PrivateRoute>} />
                <Route path="/trips" element={<PrivateRoute><Trips /></PrivateRoute>} />
                <Route path="/email-imports" element={<PrivateRoute travelerOnly><EmailImports /></PrivateRoute>} />
                <Route path="/partner/email-imports" element={<PrivateRoute><EmailImports variant="partner" /></PrivateRoute>} />
                <Route path="/trips/:id/rate" element={<PrivateRoute travelerOnly><TripRating /></PrivateRoute>} />
                <Route path="/trips/:id" element={<PrivateRoute><TripDetails /></PrivateRoute>} />
                <Route path="/trips/:id/edit" element={<PrivateRoute><EditTrip /></PrivateRoute>} />
                <Route path="/memories" element={<PrivateRoute><MemoriesList /></PrivateRoute>} />
                <Route path="/memories/new" element={<PrivateRoute><MemoriesCreate /></PrivateRoute>} />
                <Route path="/memories/j/:code" element={<MemoriesJoin />} />
                <Route path="/memories/v/:code" element={<MemoriesShare />} />
                <Route path="/memories/e/:slug/edit" element={<PrivateRoute><MemoriesCreate /></PrivateRoute>} />
                <Route path="/memories/e/:slug/add" element={<MemoriesUpload />} />
                <Route path="/memories/e/:slug" element={<MemoriesGuestOrUserRoute><MemoriesEvent /></MemoriesGuestOrUserRoute>} />
                <Route path="/deals" element={<DealsPlan />} />
                <Route path="/advisors" element={<AdvisorDirectory />} />
                <Route path="/advisors/:slug" element={<PublicAdvisorProfile />} />
                <Route path="/flights/search" element={<FlightSearchResults />} />
                <Route path="/flights/select-fare" element={<FlightFareSelector />} />
                <Route path="/flights/checkout" element={<DuffelOffer />} />
                <Route path="/duffel/offer/:offerId" element={<DuffelOffer />} />
                <Route path="/booking/confirmation" element={<PrivateRoute><FlightBookingConfirmation /></PrivateRoute>} />
                <Route path="/my-bookings" element={<PrivateRoute><MyBookings /></PrivateRoute>} />
                <Route path="/my-bookings/packages/:bookingRef" element={<PrivateRoute><MyPackageBookingDetail /></PrivateRoute>} />
                <Route path="/bookings" element={<PrivateRoute><MyBookings /></PrivateRoute>} />
                <Route path="/my-inquiries" element={<PrivateRoute><MyInquiries /></PrivateRoute>} />
                <Route path="/my-inquiries/:inquiryId" element={<PrivateRoute><MyInquiries /></PrivateRoute>} />
                <Route path="/booking/:bookingId" element={<PrivateRoute><BookingDetails /></PrivateRoute>} />
                <Route path="/package/:packageId" element={<PackageDetail />} />
                <Route path="/p/:catalogId" element={<PartnerCatalog />} />
                <Route path="/packages/:slug" element={<PublicPackageDetails />} />
                <Route path="/ai-concierge" element={<AIConciergeRedirect />} />
                <Route path="/support" element={<Support />} />
                <Route path="/how-to" element={<PrivateRoute travelerOnly><HowToGuide role="client" /></PrivateRoute>} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/partner-terms" element={<PartnerTerms />} />
                <Route path="/privacy" element={<Privacy />} />
                {/* Legacy public intake → Packages Private tab (gated by Admin → Features). */}
                <Route path="/private" element={<Navigate to="/deals?tab=packages&sub=private" replace />} />
                <Route path="/private-travel" element={<Navigate to="/deals?tab=packages&sub=private" replace />} />
                <Route path="/partner-intake/:token" element={<PartnerIntakePublic />} />
                <Route path="/t/:token" element={<PublicTripView />} />
                <Route
                  path="/admin"
                  element={
                    <PrivateRoute>
                      <AdminPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/partner-applications"
                  element={
                    <PrivateRoute>
                      <AdminPartnerApplications />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/campaigns"
                  element={
                    <PrivateRoute>
                      <AdminCampaigns />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/marketing-shows"
                  element={
                    <PrivateRoute>
                      <AdminMarketingShows />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/features"
                  element={
                    <PrivateRoute>
                      <AdminFeatures />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/private"
                  element={
                    <PrivateRoute>
                      <AdminPrivateTravel />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/private/inquiries"
                  element={
                    <PrivateRoute>
                      <AdminPrivateTravel />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/private/inquiries/:inquiryId"
                  element={
                    <PrivateRoute>
                      <AdminPrivateTravel />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/private/proposals"
                  element={
                    <PrivateRoute>
                      <AdminPrivateTravel />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/private/suppliers"
                  element={
                    <PrivateRoute>
                      <AdminPrivateTravel />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/private/partners"
                  element={
                    <PrivateRoute>
                      <AdminPrivateTravel />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/private/performance"
                  element={
                    <PrivateRoute>
                      <AdminPrivateTravel />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/private/settings"
                  element={
                    <PrivateRoute>
                      <AdminPrivateTravel />
                    </PrivateRoute>
                  }
                />
                <Route path="/partner/signup" element={<Register />} />
                <Route path="/partner/login" element={<PartnerLogin />} />
                <Route path="/partner/setup-password" element={<PartnerPasswordSetup />} />
                <Route path="/partner/dashboard" element={<PartnerDashboard />} />
                <Route path="/partner/profile" element={<PartnerProfile />} />
                <Route path="/partner/trips" element={<PartnerTrips />} />
                <Route path="/partner/trips/:tripId" element={<PartnerTripDetails />} />
                <Route path="/partner/packages" element={<PartnerPackages />} />
                <Route path="/partner/packages/dmcs" element={<PartnerDmcs />} />
                <Route path="/partner/packages/create" element={<PartnerPackageEditor />} />
                <Route path="/partner/packages/:packageId" element={<PartnerPackageEditor />} />
                <Route path="/partner/packages/:packageId/edit" element={<PartnerPackageEditor />} />
                <Route path="/partner/inventory" element={<PartnerInventory />} />
                <Route path="/partner/add-ons" element={<PartnerAddOns />} />
                <Route path="/partner/analytics" element={<PartnerAnalytics />} />
                <Route path="/partner/inquiries" element={<PartnerInquiries />} />
                <Route path="/partner/inquiries/:inquiryId" element={<PartnerInquiries />} />
                <Route path="/partner/private-referrals" element={<PartnerPrivateReferrals />} />
                <Route path="/partner/requests" element={<PartnerRequests />} />
                <Route path="/partner/bookings" element={<PartnerBookings />} />
                <Route path="/partner/bookings/:bookingId" element={<PartnerBookings />} />
                <Route path="/partner/team" element={<PartnerTeam />} />
                <Route path="/partner/clients" element={<PartnerClients />} />
                <Route path="/partner/clients/:clientId" element={<PartnerClientDetail />} />
                <Route path="/partner/billing" element={<PartnerBilling />} />
                <Route path="/partner/how-to" element={<HowToGuide role="partner" />} />
                <Route path="/partner/master-trips" element={<PartnerMasterTrips />} />
                <Route path="/partner/master-trips/:id" element={<PartnerMasterTripDetail />} />
                <Route path="/developer/*" element={<DeveloperHostRoutes nested />} />
                <Route path="/embed/catalog/:token" element={<CatalogEmbed />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/payment/failed" element={<PaymentFailed />} />
                <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <AppPageFooter />
                    </>
                    )}
                    </ClientBrandingProvider>
                  </Router>
                </SnackbarProvider>
                      </ThemeProvider>
                    </BypassProvider>
                  </PaymentProvider>
              </UnreadMessagesProvider>
        </GlobalWebSocketProvider>
      </AuthProvider>
      
      {/* Terms of Service Modal */}
      <TermsModal
        open={showTermsModal}
        onClose={handleCloseTermsModal}
        onAccept={handleAcceptTerms}
      />
    </HelmetProvider>
    </GoogleOAuthProvider>
  );
}

export default App; 